import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { ReviewsMobileViewCell } from 'Deer/components/Reviews/ReviewsMobileViewCell';
import { ALL_VIEWS_TIMELINE } from 'Deer/intent/Reviews/components/ReviewManageTimelineFilter';
import {
  useSelectReviewsSelectedForm,
  useSelectReviewsSelectedIntent,
  useSelectedReviewCompanies,
  useSelectedReviewLocations,
} from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useSelector } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import 'styles/agGrid.css';
import { checkReviewViewBasedFilterEnabled, checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { isMobileView } from 'constants/isMobileView';
import {
  sendPPCheckboxSelectionEvent,
  sendPPSelectCurrentPageEmployeesEvent,
} from 'constants/mixpanelHelpers/partialPublishEvents';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { useGetReviewsManageTabularDataQueryV1 } from '../../dataHooks';
import { useBulkActionContext } from '../ReviewManageTable/components/BulkActions/BulkActionContext';
import {
  REVIEW_STATUS_COLORS,
  REVIEW_STATUS_LABELS,
} from '../ReviewManageTable/components/ShareResultsDrawer/PartialPublishContext';
import { useSetLocationMetaFilterInitialData, useSetCompanyMetaFilterInitialData } from './hooks/reviewAGGridHook';
import { REVIEW_MANAGE_GRID_COLUMN_ID } from './reviewMangeGridConstants';
import { hideReviewManageColumn, lockVisibleReviewManageColumn } from './utils';

export const GRID_COLOR_MAPPING = {
  grey: '#333333',
  green: '#1A5724',
  orange: '#B96400',
  red: '#C62C02',
  blue: '#1565BC',
};

export const RELATIONSHIP_COL_KEYS = {
  DLR: 'DLR',
  TEAM_MEMBER: 'TEAM_MEMBER',
  OTHERS: 'OTHERS',
};

export function prepareTableData(cols, data) {
  const tableRowData = {};
  cols.forEach((col) => {
    tableRowData[col.columnId] = data[col.columnId];
  });

  return tableRowData;
}

const MobileCellRenderer = ({ data, selectedReviewIntent, hasReviewOutcome }) => {
  if (!data) return null;

  // CALIBRATION STATUS
  if (selectedReviewIntent === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS) {
    const displayName = data?.EMPLOYEE?.displayName ?? '';
    const profilePhoto = data?.EMPLOYEE?.profilePhoto ?? '';
    const relationship = data?.RELATIONSHIP ?? '';
    let description = { title: 'Overall rating', text: data?.OVERALL_RATING?.ratingText ?? 'NA' };

    if (hasReviewOutcome) {
      description = { title: 'Review outcome', text: data?.OUTCOME?.ratingText ?? 'NA' };
    }

    return (
      <ReviewsMobileViewCell
        name={displayName}
        profilePhoto={profilePhoto}
        subTitle={relationship}
        description={description}
      />
    );
  }

  // REVIEW STATUS
  const displayName = data?.EMPLOYEE?.displayName ?? '';
  const profilePhoto = data?.EMPLOYEE?.profilePhoto ?? '';
  const relationship = data?.RELATIONSHIP ?? '';
  const reviewStatus = data?.REVIEW_STATUS ?? '';

  return (
    <ReviewsMobileViewCell
      name={displayName}
      profilePhoto={profilePhoto}
      subTitle={relationship}
      description={{ title: 'Review status' }}
      tagProps={{ color: REVIEW_STATUS_COLORS[reviewStatus], text: REVIEW_STATUS_LABELS[reviewStatus] }}
    />
  );
};

function ReviewManageAGGridV1({ rowAction, agGridRef, gridStatePersistenceKey, showAICalibration }) {
  const { isFetching, data: reviewsManageData } = useGetReviewsManageTabularDataQueryV1();
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);
  const orgId = loggedInEmployeeData?.organization?.id;
  const selectedForm = useSelectReviewsSelectedForm();
  const selectedCompanies = useSelectedReviewCompanies();
  const selectedLocations = useSelectedReviewLocations();

  const { columns } = reviewsManageData ?? {};
  const rowsData = useMemo(() => {
    let rowsDataBeforeFilters = reviewsManageData?.rows || [];

    if (checkEnableCompanySupport()) {
      const selectedCompaniesMap = {};
      const selectedLocationsMap = {};
      selectedCompanies?.forEach((company) => {
        selectedCompaniesMap[company.id] = company;
      });
      selectedLocations?.forEach((location) => {
        // map keys must be in lowercase to match the case sensitivity of the data
        selectedLocationsMap[location?.toLowerCase()] = location;
      });

      rowsDataBeforeFilters = rowsDataBeforeFilters?.filter(
        (row) => selectedCompaniesMap[row?.COMPANY?.id] && selectedLocationsMap[row?.LOCATION?.toLowerCase()]
      );
    }
    if (
      !checkReviewViewBasedFilterEnabled() ||
      (checkReviewViewBasedFilterEnabled() &&
        (selectedForm?.feedbackFormId === ALL_VIEWS_TIMELINE.feedbackFormId || !selectedForm?.feedbackFormId)) // if ALL VIEWS then use the original data
    )
      return rowsDataBeforeFilters;

    return rowsDataBeforeFilters?.filter((row) => row?.FEEDBACK_FORM_ID === selectedForm?.feedbackFormId);
  }, [reviewsManageData?.rows, selectedForm?.feedbackFormId, selectedLocations, selectedCompanies]);

  useSetCompanyMetaFilterInitialData(reviewsManageData);
  useSetLocationMetaFilterInitialData(reviewsManageData);

  const [columnDefs, setColumnDefs] = useState([]);

  const { selectedEmployeeIds, setSelectedEmployeeIds, resetSelection, setTotalEmployees } = useBulkActionContext();

  const {
    tableColumnsToSelect,
    tableColumnsLockVisible,
    columnDefaultOrder,
    id: selectedReviewIntent,
  } = useSelectReviewsSelectedIntent() ?? {};

  // related to side panel , needs vikalp updated code
  // useEffect(() => {
  //   // sync selected row data in store, as the same data is used in for some things in calibration panel
  //   if (tableRowData?.[TABLE_COLUMNS_DISPLAYNAME.REVIEWEE_NAME]) {
  //     const selectedRowDataValues = rowsData?.find(
  //       (row) => row?.EMPLOYEE.id === tableRowData[TABLE_COLUMNS_DISPLAYNAME.REVIEWEE_NAME].uuid
  //     );

  //     if (!selectedRowDataValues) return;

  //     const newTableRowData = prepareTableData(columns, selectedRowDataValues);
  //     dispatch(
  //       setTableRowData({
  //         ...newTableRowData,
  //         ...getReviewManageRowDerivedData({ rowData: newTableRowData, performanceGridData }),
  //       })
  //     );
  //   }
  //   // do not add tableRowData as a dependency, as it will cause infinite loop
  // }, [columns, dispatch, performanceGridData, rowsData]);

  useEffect(() => {
    if (isFetching) {
      agGridRef.current?.api?.setGridOption('loading', true);
    } else {
      agGridRef.current?.api?.setGridOption('loading', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agGridRef.current, isFetching]);

  useEffect(() => {
    if (selectedEmployeeIds?.length === 0) {
      agGridRef.current?.api?.deselectAll();
    }
  }, [agGridRef, selectedEmployeeIds.length]);

  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const newColumnDefs = columns
      .map((col) => {
        const colProps = col;
        return {
          colId: colProps.columnId,
          field: colProps.columnId,
          id: colProps.columnId,
          displayName: colProps.displayName,
          description: colProps.description,
          minWidth: 220,
          hide: hideReviewManageColumn({
            colId: colProps.columnId,
            displayName: colProps.displayName,
            firstTimePinned: [].includes(colProps.columnId) ? true : col.firstTimePinned,
            orgId,
            tableColumnsToSelect,
          }),
          lockVisible: lockVisibleReviewManageColumn({
            displayName: colProps.displayName,
            tableColumnsLockVisible,
            columnId: colProps.columnId,
          }),
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
          },
          // @todo:to be fixed gracefully on BE and BE by renaming displayName in api and in FE hardcoded ENUM TABLE_COLUMNS_DISPLAYNAME
          // https://linear.app/peoplemesh/issue/ENG-15933/issueor-ag-gird-ratings-are-showing-error-for-some-columns
          // headerValueGetter: () => col.displayName,
          headerValueGetter: () => colProps.displayName?.replace(/QnA/i, 'Q&A'),

          // ...getFeatureSpecificColumnDefinitionByType(col),
          ...getColumnDefinitionByType(colProps),
          ...(colProps.columnId === REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE
            ? {
                headerCheckboxSelection: !isMobileView(),
                headerCheckboxSelectionCurrentPageOnly: !isMobileView(),
                checkboxSelection: !isMobileView(),
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'locked-col',
              }
            : {}),
          ...(colProps.columnId === REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION
            ? {
                filterParams: {
                  values: ['Yes', 'No'],
                },
              }
            : {}),
        };
      })
      .filter((col) => {
        // Handle special column visibility conditions
        switch (col.colId) {
          // show "Identified For Calibration" column if feature flag is on
          case REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION: {
            return showAICalibration;
          }
          // show "Calibration Status" column only if the selected intent is "Calibration Results"
          case REVIEW_MANAGE_GRID_COLUMN_ID.CALIBRATION_STATUS: {
            return selectedReviewIntent === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS;
          }
          default: {
            return true;
          }
        }
      });

    const columnIdToDefinitionMap = {};
    newColumnDefs.forEach((colDef) => {
      const { colId } = colDef;
      columnIdToDefinitionMap[colId] = colDef;
    });

    // set the default order of columns, this columnDefaultOrder comes from metadata in intent just like tableColumnsToSelect
    const columnDefInDefaultOrder = columnDefaultOrder
      .map((colId) => columnIdToDefinitionMap[colId] ?? null)
      .filter((colDef) => !!colDef); //this is for the possible case when column is removed from BE api call
    setColumnDefs(columnDefInDefaultOrder);
  }, [columns]);

  const hasReviewOutcome = useMemo(() => rowsData?.some((row) => row?.OUTCOME), [rowsData]);

  const responsiveColumnDefs = useMemo(
    () => [
      {
        colId: REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE,
        field: REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE,
        headerName: 'Employee',
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => (
          <MobileCellRenderer
            data={params.data}
            selectedReviewIntent={selectedReviewIntent}
            hasReviewOutcome={hasReviewOutcome}
          />
        ),
        minWidth: 220,
        flex: 1,
      },
    ],
    [selectedReviewIntent, hasReviewOutcome]
  );

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  function handleRowClick(data, cols) {
    // to be uncommented when deeplinking is enabled
    // if ( data['1']?.uuid) {
    //   openReviewManageSidepanel({
    //     reviewManageEmpId: data?.['1']?.uuid,
    //     reviewManageReviewId: selectedReviewCycle?.uuid,
    //   });
    //   return;
    // }
    rowAction(prepareTableData(cols, data));
  }

  const { module, page, empId } = usePartialPublishEventDetails();

  const onSelectionChanged = useCallback(
    (event) => {
      if (event.source === 'uiSelectAllCurrentPage') {
        sendPPSelectCurrentPageEmployeesEvent({
          eventProperties: {
            module,
            page,
            empId,
          },
        });
      }
      if (event.source === 'checkboxSelected') {
        sendPPCheckboxSelectionEvent({
          eventProperties: {
            module,
            page,
            empId,
          },
        });
      }
      setSelectedEmployeeIds(event.api.getSelectedRows().map((e) => +(e?.EMPLOYEE?.id || '')));
    },
    [empId, module, page, setSelectedEmployeeIds]
  );

  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    if (agGridRef.current?.api?.isAnyFilterPresent) {
      agGridRef.current?.api?.forEachNodeAfterFilter(() => {
        total += 1;
      });
    } else {
      total = rowsData?.length;
    }
    setTotalEmployees(total);
  }, [agGridRef, rowsData?.length, setTotalEmployees]);

  const onRowDataUpdated = useCallback(() => {
    resetSelection();
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange, resetSelection]);

  return (
    <Box className="review">
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={agGridRef}
        rowData={rowsData}
        columnDefs={isMobileView() ? responsiveColumnDefs : columnDefs}
        domLayout="autoHeight"
        pagination
        suppressPaginationPanel={rowsData?.length < 6 || rowsData?.length === undefined || rowsData?.length === 0}
        paginationPageSize={isMobileView() ? 5 : 10}
        sideBar={sideBar}
        headerHeight={36}
        autoHeight={false}
        rowHeight={isMobileView() ? 100 : 56}
        suppressCellFocus
        onRowClicked={(e) => handleRowClick(e.data, columns)}
        rowSelection="multiple"
        // using deprecated api, because new way has a bug, currently not working
        suppressRowClickSelection
        onSelectionChanged={onSelectionChanged}
        onRowDataUpdated={onRowDataUpdated}
        onFilterChanged={handleFilterAndDataChange}
        onFirstDataRendered={handleFilterAndDataChange}
        containerAutoHeight
        paginationPageSizeSelector={isMobileView() ? false : [10, 20, 50]}
      />
    </Box>
  );
}

export default ReviewManageAGGridV1;
